import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AnimatePresence, m } from 'motion/react'
import { CircleAlert, CircleCheck, Undo2, X } from 'lucide-react'
import { useT } from '../lib/i18n.tsx'
import { Kbd } from './Kbd.tsx'
import { cn, duration } from './util.ts'

export const UNDO_MS = 6000

export interface ToastInput {
  text: string
  /** Shows 'Undo' (and ⌘Z) for 6 seconds. */
  undo?: () => void
  /** One other action, e.g. Reload when a new version is ready. Without `undo` the toast stays until the action or Dismiss: no timer, and newer toasts never push it out. */
  action?: { label: string; run: () => void }
  tone?: 'neutral' | 'ok' | 'danger'
}
interface ToastItem extends ToastInput {
  id: number
}

/** An action-only toast (e.g. 'Update ready' with Reload) waits for its action or Dismiss. */
const waits = (x: ToastInput) => !!x.action && !x.undo

const Ctx = createContext<{ show: (t: ToastInput) => void } | null>(null)

/** useToast().show({ text: 'Touch logged', undo: () => … }) */
export function useToast() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useToast needs <ToastProvider>')
  return ctx
}

function inTextField(el: Element | null): boolean {
  return !!el && (el.matches('input, textarea, select') || (el as HTMLElement).isContentEditable)
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const t = useT()
  const [items, setItems] = useState<ToastItem[]>([])
  const seq = useRef(0)
  const timers = useRef(new Map<number, number>())

  const dismiss = useCallback((id: number) => {
    window.clearTimeout(timers.current.get(id))
    timers.current.delete(id)
    setItems((list) => list.filter((x) => x.id !== id))
  }, [])

  const show = useCallback(
    (input: ToastInput) => {
      const id = ++seq.current
      // At most three at once: the oldest of the others make room; a toast that waits is never pushed out.
      setItems((list) => {
        const others = list.filter((x) => !waits(x))
        const room = Math.max(0, 2 - (list.length - others.length))
        const kept = new Set(others.slice(Math.max(0, others.length - room)))
        return [...list.filter((x) => waits(x) || kept.has(x)), { ...input, id }]
      })
      // A waiting toast has no timer; the rest leave after 6 s.
      if (!waits(input)) timers.current.set(id, window.setTimeout(() => dismiss(id), UNDO_MS))
    },
    [dismiss],
  )

  const runUndo = useCallback(
    (item: ToastItem) => {
      item.undo?.()
      dismiss(item.id)
    },
    [dismiss],
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z' || e.shiftKey || e.isComposing) return
      if (inTextField(document.activeElement)) return
      const last = [...items].reverse().find((x) => x.undo)
      if (!last) return
      e.preventDefault()
      runUndo(last)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [items, runUndo])

  const value = useMemo(() => ({ show }), [show])

  return (
    <Ctx.Provider value={value}>
      {children}
      <div
        role="region"
        aria-label={t('common.notifications')}
        className="pointer-events-none fixed inset-x-3 bottom-tabbar-safe z-70 flex flex-col items-center gap-2 md:inset-x-auto md:right-5 md:bottom-5 md:items-end"
      >
        <AnimatePresence initial={false}>
          {items.map((item) => (
            <Toast key={item.id} item={item} onUndo={() => runUndo(item)} onDismiss={() => dismiss(item.id)} />
          ))}
        </AnimatePresence>
      </div>
    </Ctx.Provider>
  )
}

/** One toast; rendered by ToastProvider (and on its own in the kitchen sink). */
export function Toast({ item, onUndo, onDismiss, still }: { item: ToastInput; onUndo: () => void; onDismiss: () => void; still?: boolean }) {
  const t = useT()
  const d = duration('base')
  const Icon = item.tone === 'danger' ? CircleAlert : CircleCheck
  return (
    <m.div
      layout={!still}
      role="status"
      initial={still ? false : { opacity: 0, y: 12, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 6, scale: 0.98, transition: { duration: d * 0.6 } }}
      transition={{ duration: d, ease: [0.16, 1, 0.3, 1] }}
      className="pointer-events-auto relative flex w-full max-w-sm items-center gap-3 overflow-hidden rounded-lg bg-overlay py-2.5 pr-2 pl-3.5 text-sm text-ink shadow-overlay"
    >
      <Icon size={16} strokeWidth={2} aria-hidden className={cn('shrink-0', item.tone === 'danger' ? 'text-danger' : 'text-ok')} />
      <span className="min-w-0 flex-1">{item.text}</span>
      {item.undo && (
        <button
          type="button"
          onClick={onUndo}
          className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-sm px-2 font-medium text-accent-text transition hover:bg-accent-soft"
        >
          <Undo2 size={14} strokeWidth={2} aria-hidden />
          {t('common.undo')}
          <span className="hidden md:inline-flex">
            <Kbd keys={['⌘', 'Z']} tone="plain" />
          </span>
        </button>
      )}
      {item.action && (
        <button
          type="button"
          onClick={() => {
            item.action?.run()
            onDismiss()
          }}
          className="inline-flex h-7 shrink-0 items-center rounded-sm px-2 font-medium text-accent-text transition hover:bg-accent-soft"
        >
          {item.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('common.dismiss')}
        className="inline-flex size-7 shrink-0 items-center justify-center rounded-sm text-ink-faint transition hover:bg-hover hover:text-ink"
      >
        <X size={14} strokeWidth={2} aria-hidden />
      </button>
      {item.undo && !still && (
        <m.span
          aria-hidden
          className="absolute bottom-0 left-0 h-0.5 bg-accent"
          initial={{ width: '100%' }}
          animate={{ width: '0%' }}
          transition={{ duration: UNDO_MS / 1000, ease: 'linear' }}
        />
      )}
    </m.div>
  )
}
